import React, { Component } from 'react';
import { BrowserRouter as Router, Switch, Route, Redirect } from 'react-router-dom';
import PropTypes from 'prop-types';
import i18next from 'i18next';
import { connect } from 'react-redux';
import ReduxToastr from 'react-redux-toastr';
import { hasSuperPermission, hasBusinessPermission, hasBudgetsPermission, hasLoyaltyPermission } from '../helpers/permissions';

import AppContainer from './app-container/AppContainer';
import BusinessContainer from './app-business/BusinessContainer';
import BudgetsContainer from './app-budgets/BudgetsContainer';
import LoyaltyContainer from './app-loyalty/LoyaltyContainer';

import Confirm from './general/Confirm';
import Loading from './general/Loading';
import Redirector from './general/Redirector';
import SystemLocales from './general/SystemLocales';
import Login from './auth/Login';
import PasswordRecovery from './auth/PasswordRecovery';
import UserSettings from './user/Settings';
import UserPassword from './user/Password';
import BusinessHome from './app-business/home/BusinessHome';
import RestaurantDetails from './app-business/settings/RestaurantDetails';
import Orders from './app-business/orders/Orders';
import OrderDetails from './app-business/orders/OrderDetails';
import Subscriptions from './app-business/orders/Subscriptions';
import MenuEditor from './app-business/menu-editor/MenuEditor';
import Delivery from './app-business/delivery/Delivery';
import RestaurantsMetabase from './app-business/data-analysis/RestaurantsMetabase';
import PaymentsReports from './app-business/data-analysis/ReportsContainer';
import Brands from './app-business/brands/Brands';
import BrandBuilder from './app-business/brands/BrandBuilder';
import Kiosks from './app-business/kiosks/Kiosks';
import Website from './app-business/website/Website';
import TestOrder from './app-business/orders/TestOrder';
import Chain from './app-business/chains/Chain';

import BudgetsHome from './app-budgets/home/BudgetsHome';
import CompanyDetails from './app-budgets/company/CompanyDetails';
import Workers from './app-budgets/workers/Workers';
import WorkerDetails from './app-budgets/workers/WorkerDetails';
import BudgetGroups from './app-budgets/budgets/BudgetGroups';
import CompanyOrders from './app-budgets/orders/Orders';
import BudgetReport from './app-budgets/reports/BudgetReport';

import LoyaltyHome from './app-loyalty/home/LoyaltyHome';
import LoyaltyProgramDetails from './app-loyalty/loyalty-program/LoyaltyProgramDetails';
import LoyaltyProgramRestaurants from './app-loyalty/restaurants/LoyaltyProgramRestaurants';
import Members from './app-loyalty/members/Members';
import MemberDetails from './app-loyalty/members/MemberDetails';
import CampaignDetails from './app-loyalty/campaigns/CampaignDetails';
import { GlobalBenefits, PersonalBenefits } from './app-loyalty/benefits/Benefits';
import BenefitDetails from './app-loyalty/benefits/BenefitDetails';

import Tools from './admin/Tools';
import DashboardUsers from './admin/dashboardUsers/DashboardUsers';
import { setup, setCurrentRestaurant, setCurrentChainId } from '../actions';
import { getPermittedCompanies, getPermittedLoyaltyPrograms } from '../selectors';
import store from '../store';

import { library } from '@fortawesome/fontawesome-svg-core';
import { faFilter } from '@fortawesome/free-solid-svg-icons/faFilter';
import { faTimes } from '@fortawesome/free-solid-svg-icons/faTimes';
import { faMinus } from '@fortawesome/free-solid-svg-icons/faMinus';
import { faArrowsAltH } from '@fortawesome/free-solid-svg-icons/faArrowsAltH';
import { faFileExcel } from '@fortawesome/free-solid-svg-icons/faFileExcel';
import MarketingTool from './app-business/marketing/MarketingTool';
import SmartPromotions from './app-business/marketing/SmartPromotions/SmartPromotions';
import Restaurants from './app-business/home/Restaurants';
import MarketingCampaigns from './app-business/marketing/MarketingCampaigns';
import Campaigns from './app-loyalty/campaigns/Campaigns';

library.add(faFilter, faTimes, faMinus, faArrowsAltH, faFileExcel);

class App extends Component {
	componentDidMount() {
		if (this.props.token) {
			this.props.setup();
		}
	}

	render() {
		const AdminRoute = ({ component: Component, capability, ...rest }) => (
			<Route {...rest} render={props => {
				this.props.setCurrentRestaurant(null);

				const { token } = this.props;

				if (!token || !hasSuperPermission(capability)) {
					return <Redirect to={{ pathname: '/login' }} />;
				}

				return (
					<AppContainer {...props}>
						<Component {...props} />
					</AppContainer>
				);
			}} />
		);

		const BusinessRoute = ({ component: Component, adminOnly, capability, ...rest }) => (
			<Route {...rest} render={props => {
				const { setCurrentRestaurant, setCurrentChainId } = this.props;

				const { restId, chainId } = props.match.params;

				if (hasBusinessPermission(capability, chainId, restId)) {
					if (restId) {
						setCurrentRestaurant(restId);
					} else if (chainId) {
						setCurrentChainId(chainId);
					} else {
						setCurrentRestaurant(null);
					}

					return (
						<BusinessContainer {...props}>
							<Component {...props} />
						</BusinessContainer>
					);
				}

				return <Redirect to={{ pathname: '/' }} />;
			}} />
		);

		const CompanyRoute = ({ component: Component, adminOnly, capability, ...rest }) => (
			<Route {...rest} render={props => {
				this.props.setCurrentRestaurant(null);

				const { companyId } = props.match.params;
				if (hasBudgetsPermission(capability, companyId)) {
					const company = getPermittedCompanies(store.getState()).find(comp => comp.id == companyId) || {};

					return (
						<BudgetsContainer {...props} >
							<Component {...props} company={company} />
						</BudgetsContainer>
					);
				}

				return <Redirect to={{ pathname: '/' }} />;
			}} />
		);

		const LoyaltyProgramRoute = ({ component: Component, adminOnly, capability, ...rest }) => (
			<Route {...rest} render={props => {
				this.props.setCurrentRestaurant(null);

				const { loyaltyProgramId } = props.match.params;
				if (hasLoyaltyPermission(capability, loyaltyProgramId)) {
					const loyaltyProgram = getPermittedLoyaltyPrograms(store.getState()).find(loyalty => loyalty.id == loyaltyProgramId) || {};

					return (
						<LoyaltyContainer {...props} >
							<Component {...props} loyaltyProgram={loyaltyProgram} />
						</LoyaltyContainer>
					);
				}

				return <Redirect to={{ pathname: '/' }} />;
			}} />
		);

		const UserRoute = ({ component: Component, adminOnly, capability, ...rest }) => (
			<Route {...rest} render={props => {
				const { token } = this.props;

				if (!token) {
					if (props.location.search.includes('?reset=')) {
						return <Component {...props} userId={props.location.search.substring(7)} />;
					}

					return <Redirect to={{ pathname: '/login' }} />;
				}

				return (
					<AppContainer {...props} >
						<Component {...props} />
					</AppContainer>
				);
			}} />
		);

		return (
			<Router>
				<div
					dir={i18next.dir()}
					style={{ height: '100%', direction: i18next.dir() }}
				>
					<Switch>
						{/* Business  */}
						<BusinessRoute exact path="/business" component={BusinessHome} />
						<BusinessRoute exact path="/restaurant/:restId" component={RestaurantDetails} />
						<BusinessRoute capability="orders" exact path="/restaurant/:restId/orders" component={Orders} />
						<BusinessRoute capability="orders" exact path="/restaurant/:restId/orders/:orderId" component={OrderDetails} />
						<BusinessRoute capability="orders" exact path="/restaurant/:restId/subscriptions" component={Subscriptions} />
						<BusinessRoute capability="menu" exact path="/restaurant/:restId/menu-editor" component={MenuEditor} />
						<BusinessRoute capability="menu" exact path="/restaurant/:restId/testOrder" component={TestOrder} />
						<BusinessRoute capability="reports" exact path="/restaurant/:restId/reports" component={PaymentsReports} />
						<BusinessRoute capability="reports" exact path="/restaurant/:restId/metabase" component={RestaurantsMetabase} />
						<BusinessRoute capability="delivery" exact path="/restaurant/:restId/delivery" component={Delivery} adminOnly />
						<BusinessRoute capability="kiosks" exact path="/restaurant/:restId/kiosks" component={Kiosks} />
						<BusinessRoute capability="marketing" exact path="/restaurant/:restId/brands" component={Brands} />
						<BusinessRoute capability="marketing" exact path="/restaurant/:restId/brands/:brandKey" component={BrandBuilder} />
						<BusinessRoute capability="marketing" exact path="/restaurant/:restId/locales" component={SystemLocales} />
						<BusinessRoute capability="marketing" exact path="/restaurant/:restId/marketing" component={MarketingTool} />
						<BusinessRoute capability="marketing" exact path="/restaurant/:restId/marketing/smartPromotions" component={SmartPromotions} /> 
						<BusinessRoute capability="marketing" exact path="/restaurant/:restId/marketing/brands" component={Brands} />
						<BusinessRoute capability="marketing" exact path="/restaurant/:restId/marketing/brands/:brandKey" component={BrandBuilder} />
						<BusinessRoute capability="marketing" exact path="/restaurant/:restId/marketing/campaigns" component={MarketingCampaigns} />
						<BusinessRoute exact path="/chain/:chainId" component={Chain} />
						<BusinessRoute exact path="/chain/:chainId/restaurants" component={Restaurants} />
						<BusinessRoute capability="menu" exact path="/chain/:chainId/menu-editor" component={MenuEditor} />
						<BusinessRoute exact path="/chain/:chainId/website" component={Website} />
						<BusinessRoute capability="marketing" exact path="/chain/:chainId/marketing" component={MarketingTool} />
						<BusinessRoute capability="marketing" exact path="/chain/:chainId/marketing/smartPromotions" component={SmartPromotions} />
						<BusinessRoute capability="marketing" exact path="/chain/:chainId/marketing/campaigns" component={MarketingCampaigns} />
						<BusinessRoute capability="marketing" exact path="/chain/:chainId/marketing/brands" component={Brands} />
						<BusinessRoute capability="marketing" exact path="/chain/:chainId/marketing/brands/:brandKey" component={BrandBuilder} />
						<BusinessRoute capability="marketing" exact path="/chain/:chainId/locales" component={SystemLocales} />

						{/* Budgets  */}
						<CompanyRoute exact path="/budgets" component={BudgetsHome} />
						<CompanyRoute exact path="/company/:companyId" component={CompanyDetails} />
						<CompanyRoute capability="employees" exact path="/company/:companyId/workers" component={Workers} />
						<CompanyRoute capability="employees" exact path="/company/:companyId/workers/:workerId" component={WorkerDetails} />
						<CompanyRoute capability="budgets" exact path="/company/:companyId/budgetgroups" component={BudgetGroups} />
						<CompanyRoute capability="reports" exact path="/company/:companyId/report" component={BudgetReport} />
						<CompanyRoute exact path="/company/:companyId/orders" component={CompanyOrders} />

						{/* Loyalty  */}
						<LoyaltyProgramRoute exact path="/loyalty" component={LoyaltyHome} />
						<LoyaltyProgramRoute exact path="/loyaltyProgram/:loyaltyProgramId" component={LoyaltyProgramDetails} />
						<LoyaltyProgramRoute capability="restaurants" exact path="/loyaltyProgram/:loyaltyProgramId/restaurants" component={LoyaltyProgramRestaurants} />
						<LoyaltyProgramRoute capability="benefits" exact path="/loyaltyProgram/:loyaltyProgramId/globalBenefits" component={GlobalBenefits} />
						<LoyaltyProgramRoute capability="benefits" exact path="/loyaltyProgram/:loyaltyProgramId/personalBenefits" component={PersonalBenefits} />
						<LoyaltyProgramRoute capability="benefits" exact path="/loyaltyProgram/:loyaltyProgramId/benefits/:benefitId" component={BenefitDetails} />
						<LoyaltyProgramRoute capability="members" exact path="/loyaltyProgram/:loyaltyProgramId/members" component={Members} />
						<LoyaltyProgramRoute capability="members" exact path="/loyaltyProgram/:loyaltyProgramId/members/:memberId" component={MemberDetails} />
						<LoyaltyProgramRoute capability="campaigns" exact path="/loyaltyProgram/:loyaltyProgramId/campaigns" component={Campaigns} />
						<LoyaltyProgramRoute capability="campaigns" exact path="/loyaltyProgram/:loyaltyProgramId/campaigns/:campaignId" component={CampaignDetails} />

						{/* Admin */}
						<AdminRoute capability="panelUsers" exact path="/admin/users" component={DashboardUsers} />
						<AdminRoute capability="tools" exact path="/admin/tools" component={Tools} />
						<AdminRoute capability="developer" exact path="/admin/locales" component={SystemLocales} />

						<Route exact path="/login" render={props => <Login {...props} />} />
						<Route exact path="/password/recovery" render={props => <PasswordRecovery {...props} />} />
						<Route exact path="/" render={(props) => <Redirector {...props} />} />
						<UserRoute exact path="/user/settings" component={UserSettings} />
						<UserRoute exact path="/user/password" component={UserPassword} />
						<Route render={(props) => <Redirector {...props} />} />
					</Switch>

					<Confirm  />
					<Loading />

					<ReduxToastr
						timeOut={3000}
						preventDuplicates
						position="top-right"
						transitionIn="fadeIn"
						transitionOut="fadeOut"
					/>

				</div>
			</Router >
		);
	}
}

App.propTypes = {
	token: PropTypes.string,
	setCurrentRestaurant: PropTypes.func,
	setCurrentChainId: PropTypes.func,
	setup: PropTypes.func
};

const mapStateToProps = (state) => {
	const { token } = state.auth;
	const { language } = state.app;

	return { token, language };
};

export default connect(mapStateToProps, { setup, setCurrentRestaurant, setCurrentChainId })(App);
